// Ticket 5 ("Route lines: three distinct states, round caps and curves"), transport-lines.md. Before
// this, renderBookedSegments and renderOptionSegments (MapContents.tsx) had identical paint — a booked
// leg and an option were indistinguishable — and renderNonBookedSegments's dashed grey nearly matched
// MarkerConnectorLines.tsx's own dashed grey despite meaning something completely different.
// "dayRoute" is not a transport: the dashed line through the numbered places of the day being read
// (share page, Day by day, sheet 195 "reading Day 1").
export type TransportLineState = "booked" | "option" | "nothingChosen" | "dayRoute";

export const TRANSPORT_LINE_COLORS: Record<TransportLineState, string> = {
	booked: "#EA5681",
	option: "#0f3e4a",
	// Moved from #658C96 (2.22 contrast on the Native basemap's tightest ground, the national-park
	// green #a0d5bc — below the floor) to #557177 (3.17): same hue, same saturation, four steps darker.
	// The booked pink measures 2.07 on the same scale but is NOT being changed — it has shipped for
	// years without anyone calling it illegible, which is the clearest available demonstration that
	// luminance contrast understates a saturated line. A neutral line has no hue doing that work, so
	// for this grey-blue the number does mean what it says. Kept as a named constant either way — it
	// is the value most sensitive to a future basemap change.
	nothingChosen: "#557177",
	dayRoute: "#0f3e4a",
};

export interface TransportLinePaint {
	"line-color": string;
	"line-width": number;
	"line-opacity": number;
	"line-dasharray"?: number[];
	"line-dasharray-transition": { duration: number; delay: number };
}

export interface TransportLineLayout {
	"line-cap": "round" | "butt";
	"line-join": "round";
}

// prototype.html's lineSpecs()/layer setup sets this on every dashed line — a booking/option status
// can change while the map is open, and the Layer id (`${name}-${index}`) can end up reused for a
// different segment, so without it a dasharray change animates instead of switching instantly.
const NO_DASH_TRANSITION = { duration: 0, delay: 0 };

export function getTransportLinePaint(state: TransportLineState): TransportLinePaint {
	switch (state) {
		case "booked":
			return { "line-color": TRANSPORT_LINE_COLORS.booked, "line-width": 4, "line-opacity": 1, "line-dasharray-transition": NO_DASH_TRANSITION };
		case "option":
			return {
				"line-color": TRANSPORT_LINE_COLORS.option,
				"line-width": 3,
				"line-opacity": 1,
				"line-dasharray": [1.2, 1.2],
				"line-dasharray-transition": NO_DASH_TRANSITION,
			};
		case "nothingChosen":
			// A dash of 0.001 with a round cap renders as a dot with diameter equal to the line width —
			// how "nothing chosen" gets its round dots without a symbol layer.
			return {
				"line-color": TRANSPORT_LINE_COLORS.nothingChosen,
				"line-width": 3,
				"line-opacity": 1,
				"line-dasharray": [0.001, 2.2],
				"line-dasharray-transition": NO_DASH_TRANSITION,
			};
		case "dayRoute":
			// The artboard's polyline: 2 wide, dashes of 5 and gaps of 6 (dasharray counts in line widths).
			return {
				"line-color": TRANSPORT_LINE_COLORS.dayRoute,
				"line-width": 2,
				"line-opacity": 0.85,
				"line-dasharray": [2.5, 3],
				"line-dasharray-transition": NO_DASH_TRANSITION,
			};
	}
}

export function getTransportLineLayout(state: TransportLineState): TransportLineLayout {
	// Option's dashes need crisp ends, so it alone stays butt; every other state (including the round
	// dots, which need the cap to actually render as a circle) is round.
	return { "line-cap": state === "option" ? "butt" : "round", "line-join": "round" };
}
